import { useEffect, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { useDemItemImageUrl } from '../../hooks/useDemItemImageUrl';
import { invalidateDemItemImageCache } from '../../lib/demItemImage';
import { itemHasPhoto, removeDemItemPhoto, uploadDemItemPhoto } from '../../lib/demItems';
import type { DemItem } from '../../types/dem';

type DemItemPhotoModalProps = {
  item: DemItem | null;
  open: boolean;
  onClose: () => void;
  onUpdated: (item: DemItem) => void;
};

export function DemItemPhotoModal({ item, open, onClose, onUpdated }: DemItemPhotoModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const displayImageUrl = useDemItemImageUrl(item?.image_url);

  useEffect(() => {
    setImageLoadError(false);
    setStatusMessage(null);
  }, [item?.code, item?.image_url, displayImageUrl, open]);

  if (!item) return null;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setPhotoBusy(true);
    setStatusMessage(null);
    try {
      const imageUrl = await uploadDemItemPhoto(item.code, file);
      invalidateDemItemImageCache(imageUrl);
      onUpdated({ ...item, image_url: imageUrl });
      setStatusMessage('Photo uploaded.');
    } catch (e) {
      setStatusMessage(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!itemHasPhoto(item)) return;
    if (!window.confirm(`Remove the photo for ${item.code}?`)) return;

    setPhotoBusy(true);
    setStatusMessage(null);
    try {
      const updated = await removeDemItemPhoto(item.code);
      onUpdated(updated);
      setStatusMessage('Photo removed.');
    } catch (e) {
      setStatusMessage(e instanceof Error ? e.message : 'Failed to remove photo.');
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Item · ${item.code}`} wide>
      <div className="dem-item-photo-modal">
        <p className={`dem-item-photo-modal__title${item.is_blocked ? ' dem-item-photo-modal__title--blocked' : ''}`}>
          {item.is_blocked ? 'Blocked · ' : ''}
          {item.description || item.code}
        </p>
        {item.chinese_description?.trim() ? (
          <p className="dem-item-photo-modal__secondary">{item.chinese_description}</p>
        ) : null}

        <div className="dem-item-photo-modal__meta">
          {item.uom ? <span>UOM: {item.uom}</span> : null}
          {item.packing ? <span>Packing: {item.packing}</span> : null}
          {item.stock_status ? <span>Stock: {item.stock_status}</span> : null}
        </div>

        <div className="dem-item-photo-modal__image-wrap">
          {itemHasPhoto(item) ? (
            displayImageUrl && !imageLoadError ? (
              <img
                key={displayImageUrl}
                src={displayImageUrl}
                alt={item.code}
                className="dem-item-photo-modal__image"
                onError={() => setImageLoadError(true)}
              />
            ) : imageLoadError ? (
              <div className="dem-item-photo-modal__placeholder">Failed to load image</div>
            ) : (
              <div className="dem-item-photo-modal__placeholder">Loading image…</div>
            )
          ) : (
            <div className="dem-item-photo-modal__placeholder">No photo available</div>
          )}
        </div>

        {statusMessage ? (
          <p className="dem-uid-status dem-uid-status--ok" role="status">
            {statusMessage}
          </p>
        ) : null}

        <div className="dem-item-photo-modal__actions">
          <button
            type="button"
            className="btn-secondary btn-sm dem-item-btn-upload"
            disabled={photoBusy}
            onClick={handleUploadClick}
          >
            {photoBusy ? 'Working…' : 'Upload photo'}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm dem-item-btn-remove"
            disabled={photoBusy || !itemHasPhoto(item)}
            onClick={() => void handleRemovePhoto()}
          >
            Remove photo
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          className="dem-item-file-input"
          onChange={(e) => void handleFileChange(e)}
        />
      </div>
    </Modal>
  );
}
